// src/lib/achievements.js
// Helpers to award + compute level milestones (idempotent + RLS-safe)

import {
  LEVEL_THRESHOLDS as LEVEL_THRESHOLDS_L2PLUS,
  levelFromXp,
} from "./levels";

function safeMeta(meta) {
  return meta && typeof meta === "object" ? meta : {};
}

/**
 * Keep a "Level 1 starts at 0 XP" view for any UI that expects it.
 * levels.js stores thresholds for Level 2+ only.
 */
export const LEVEL_THRESHOLDS = [0, ...LEVEL_THRESHOLDS_L2PLUS];

export function xpToLevel(totalXp) {
  return levelFromXp(Number(totalXp) || 0, LEVEL_THRESHOLDS_L2PLUS);
}

export async function ensureUserAchievement(
  supabase,
  { key, meta = {} },
  onLog
) {
  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();
  if (authErr) throw authErr;
  if (!user) throw new Error("Not authenticated");

  const payload = {
    user_id: user.id,
    achievement_key: key,
    meta: safeMeta(meta),
  };

  const { data: inserted, error: insErr } = await supabase
    .from("user_achievements")
    .insert(payload)
    .select("achievement_key, unlocked_at, meta")
    .maybeSingle();

  if (!insErr) {
    onLog?.({
      at: "ensureUserAchievement",
      action: "insert",
      key,
      ok: true,
      isNew: true,
      data: inserted,
    });
    return { ...inserted, isNew: true };
  }

  if (insErr.code === "23505" || insErr.code === "409") {
    const { data: existing, error: selErr } = await supabase
      .from("user_achievements")
      .select("achievement_key, unlocked_at, meta")
      .eq("user_id", user.id)
      .eq("achievement_key", key)
      .maybeSingle();

    if (selErr) {
      onLog?.({
        at: "ensureUserAchievement",
        action: "select_existing",
        key,
        ok: false,
        error: selErr,
      });
      throw selErr;
    }

    onLog?.({
      at: "ensureUserAchievement",
      action: "select_existing",
      key,
      ok: true,
      isNew: false,
      data: existing,
    });
    return { ...existing, isNew: false };
  }

  onLog?.({ at: "ensureUserAchievement", action: "insert", key, ok: false, error: insErr });
  throw insErr;
}

export async function checkAndAwardAchievementsOnResult(
  supabase,
  { questId, subquestId, levelKey, passed },
  onLog
) {
  const newlyUnlocked = [];
  const log = (k, v) => onLog?.({ at: k, ...v });

  try {
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr) throw authErr;
    if (!user) return newlyUnlocked;

    if (!passed) return newlyUnlocked;

    // 1) First level complete
    const { data: anyLevel, error: lpErr } = await supabase
      .from("lesson_progress")
      .select("id")
      .eq("user_id", user.id)
      .eq("completed", true)
      .limit(2);

    if (!lpErr) {
      const count = anyLevel?.length || 0;
      log("first_level_check", { count });

      if (count === 1) {
        const res = await ensureUserAchievement(
          supabase,
          {
            key: "first_level_complete",
            meta: {
              questId: String(questId),
              subquestId: String(subquestId),
              levelKey: String(levelKey),
            },
          },
          onLog
        );
        if (res?.isNew) newlyUnlocked.push("first_level_complete");
      }
    }

    // 2) First subquest complete
    const { data: lessons, error: lErr } = await supabase
      .from("lessons")
      .select("id")
      .eq("quest_id", String(questId))
      .eq("subquest_id", String(subquestId));

    if (!lErr) {
      const subIds = (lessons || []).map((l) => l.id);
      if (subIds.length) {
        const { data: progress, error: pErr } = await supabase
          .from("lesson_progress")
          .select("lesson_id, completed")
          .eq("user_id", user.id)
          .in("lesson_id", subIds);

        if (!pErr) {
          const allPassed =
            (progress || []).length === subIds.length &&
            (progress || []).every((p) => p.completed);

          log("subquest_check", {
            total: subIds.length,
            completed: (progress || []).filter((p) => p.completed).length,
            allPassed,
          });

          if (allPassed) {
            const res = await ensureUserAchievement(
              supabase,
              {
                key: "first_subquest_complete",
                meta: { questId: String(questId), subquestId: String(subquestId) },
              },
              onLog
            );
            if (res?.isNew) newlyUnlocked.push("first_subquest_complete");
          }
        }
      }
    }

    // 3) Quest completion
    const questKey = String(questId);
    const questAchievementKey =
      questKey === "1"
        ? "quest1_complete"
        : questKey === "2"
        ? "quest2_complete"
        : questKey === "3"
        ? "quest3_complete"
        : null;

    if (questAchievementKey) {
      const { data: qLessons, error: qErr } = await supabase
        .from("lessons")
        .select("id")
        .eq("quest_id", questKey);

      if (!qErr) {
        const qIds = (qLessons || []).map((l) => l.id);
        if (qIds.length) {
          const { data: qProg, error: qpErr } = await supabase
            .from("lesson_progress")
            .select("lesson_id, completed")
            .eq("user_id", user.id)
            .in("lesson_id", qIds);

          if (!qpErr) {
            const allPassedQ =
              (qProg || []).length === qIds.length &&
              (qProg || []).every((r) => r.completed);

            log("quest_check", {
              questId: questKey,
              total: qIds.length,
              completed: (qProg || []).filter((p) => p.completed).length,
              allPassed: allPassedQ,
            });

            if (allPassedQ) {
              const res = await ensureUserAchievement(
                supabase,
                { key: questAchievementKey, meta: { questId: questKey } },
                onLog
              );
              if (res?.isNew) newlyUnlocked.push(questAchievementKey);
            }
          }
        }
      }
    }

    // 4) Level milestones
    const { data: xpRows, error: xpErr } = await supabase
      .from("lesson_progress")
      .select("xp_earned")
      .eq("user_id", user.id);

    if (!xpErr) {
      const totalXp = (xpRows || []).reduce((s, r) => s + (r.xp_earned || 0), 0);
      const L = xpToLevel(totalXp);
      log("level_milestone", { totalXp, level: L });

      if (L >= 3) {
        const res = await ensureUserAchievement(
          supabase,
          { key: "level_3", meta: { totalXp, level: L } },
          onLog
        );
        if (res?.isNew) newlyUnlocked.push("level_3");
      }
      if (L >= 6) {
        const res = await ensureUserAchievement(
          supabase,
          { key: "level_6", meta: { totalXp, level: L } },
          onLog
        );
        if (res?.isNew) newlyUnlocked.push("level_6");
      }
      if (L >= 9) {
        const res = await ensureUserAchievement(
          supabase,
          { key: "level_9", meta: { totalXp, level: L } },
          onLog
        );
        if (res?.isNew) newlyUnlocked.push("level_9");
      }
    }

    return newlyUnlocked;
  } catch (e) {
    console.warn("checkAndAwardAchievementsOnResult failed:", e);
    onLog?.({ at: "checkAndAwardAchievementsOnResult", ok: false, error: e });
    return newlyUnlocked;
  }
}